/**
 * Enum for Airwallex SCA Verify Error Codes
 * @enum {string}
 * @readonly
 */
export const ScaVerifyErrorCodes = Object.freeze({
  MISSING: 'sca_token_missing',
  INVALID: 'sca_token_invalid',
  INSUFFICIENT: 'sca_token_insufficient',
});

/**
 * Enum for Airwallex Transaction Operations
 * @enum {string}
 * @readonly
 */
export const ScaTransactionOperations = Object.freeze({
  BALANCES: 'BALANCES',
  BATCH_TRANSFERS: 'BATCH_TRANSFERS',
});

/**
 * The URL for the Airwallex Sign Up Terms and Conditions page.
 *
 * @constant
 * @type {string}
 * @see https://www.airwallex.com/terms/sign-up-terms
 *
 * This constant should be used wherever the application needs to reference
 * the Airwallex terms and conditions.
 */
export const AIRWALLEX_TERMS_AND_CONDITIONS_URL = 'https://www.airwallex.com/terms/sign-up-terms';

/**
 * Enum for Airwallex SCA element event names.
 *
 * This helps avoid typos and provides auto-completion when attaching event listeners
 * to Airwallex SCA elements.
 *
 * @readonly
 * @enum {string}
 */
export const ScaElementEvents = Object.freeze({
  READY: 'ready',
  SCA_SETUP_SUCCEED: 'scaSetupSucceed',
  VERIFICATION_SUCCEED: 'verificationSucceed',
  VERIFICATION_FAILED: 'verificationFailed',
  ERROR: 'error',
  CANCEL: 'cancel',
});

/**
 * Enum for Airwallex Beneficiary element event names.
 *
 * This helps avoid typos and provides auto-completion when attaching event listeners
 * to Airwallex Beneficiary elements.
 *
 * @readonly
 * @enum {string}
 */
export const BeneficiaryElementEvents = Object.freeze({
  READY: 'ready',
  SUCCESS: 'success',
  ERROR: 'error',
});

/**
 * Enum for Airwallex KYC element event names.
 *
 * This helps avoid typos and provides auto-completion when attaching event listeners
 * to Airwallex KYC elements.
 *
 * @readonly
 * @enum {string}
 */
export const KycElementEvents = Object.freeze({
  READY: 'ready',
  SUCCESS: 'success',
  ERROR: 'error',
});

/**
 * Enum for Airwallex KYC ready handler event names.
 *
 * This helps avoid typos and provides auto-completion when attaching event listeners
 * to Airwallex KYC elements.
 *
 * @readonly
 * @enum {string}
 */
export const KycElementReadyHandlerEvents = Object.freeze({
  INIT: 'INIT',
  SUBMITTED: 'SUBMITTED',
  SUCCESS: 'SUCCESS',
  FAILED: 'FAILED',
});

/**
 * Enum for Airwallex financial report types
 * @enum {string}
 * @readonly
 */
export const FinancialReportType = Object.freeze({
  ACCOUNT_STATEMENT_REPORT: 'ACCOUNT_STATEMENT_REPORT',
  BALANCE_ACTIVITY_REPORT: 'BALANCE_ACTIVITY_REPORT',
});

/**
 * Labels for Airwallex financial report types
 * @enum {string}
 * @readonly
 */
export const FinancialReportTypeLabels = Object.freeze({
  [FinancialReportType.BALANCE_ACTIVITY_REPORT]: 'Balance activity report',
  [FinancialReportType.ACCOUNT_STATEMENT_REPORT]: 'Account statement',
});

/**
 * Descriptions for Airwallex financial report types
 * @enum {string}
 * @readonly
 */
export const FinancialReportTypeDescriptions = Object.freeze({
  [FinancialReportType.BALANCE_ACTIVITY_REPORT]: 'Activity report for account balance',
  [FinancialReportType.ACCOUNT_STATEMENT_REPORT]: 'Activity report with additional account details',
});

/**
 * File format (extension + content type) for each Airwallex financial report type
 * @enum {{ extension: string, contentType: string }}
 * @readonly
 */
export const FinancialReportFileFormats = Object.freeze({
  [FinancialReportType.BALANCE_ACTIVITY_REPORT]: { extension: 'CSV', contentType: 'text/csv' },
  [FinancialReportType.ACCOUNT_STATEMENT_REPORT]: { extension: 'PDF', contentType: 'application/pdf' },
});

/**
 * Enum for Airwallex financial report statuses
 * @enum {string}
 * @readonly
 */
export const FinancialReportStatus = Object.freeze({
  PENDING: 'PENDING',
  COMPLETED: 'COMPLETED',
});
